import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { Box, Divider, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { type FC, type Ref, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AccentIconTile } from "../../../components/UIToolkit/molecules/AccentIconTile";
import { PanelHeader } from "../../../components/UIToolkit/molecules/PanelHeader";
import { MOTION } from "../../../theme/tokens";
import type { PbjCategoryScore } from "../../../utils/models";
import { cardShadow } from "./colors";
import { computeCumulativePass, DAYS_PER_WEEK_FULL, DAYS_PER_WEEK_WEEKEND } from "./cumulativeScoring.utils";
import { WEEKEND_REC_IDS } from "./planOutcome.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import type { PlanItem } from "./recommendations.constants";

type Props = {
	selectedPlan: PlanItem[];
	// HPPD categories (with band ladders + rawValue baselines) the cumulative
	// scoring pass scores each plan item's marginal points against.
	categories: PbjCategoryScore[];
	onRemove: (id: PlanItem["id"]) => void;
	// Census + per-day-type quarter counts let us convert each item's per-PPD
	// spend rate into its real projected dollar cost. Weekday items multiply by
	// `quarterDays`; weekend items multiply by `quarterWeekendDays` because the
	// hours only land on weekend shifts (the spend rate is per-weekend-PPD).
	census: number;
	quarterDays: number;
	quarterWeekendDays: number;
	// Remaining calendar-day count (anchored to the calendar quarter end). Used to
	// switch PPD math onto a catch-up basis when the period toggle is set to "qtd".
	daysRemaining: number;
	// Drives which hour rate + action label variant each plan item shows: "quarter"
	// = sustained per-day rate; "qtd" = catch-up rate over remaining days only.
	periodMode: PeriodMode;
	// Forwarded to the card root so the owner can scroll a freshly-added action into view.
	// Lives on the Paper (not a wrapper) so this card can be a direct grid cell that simply
	// disappears — leaving no empty cell — when the plan is empty and the component returns null.
	scrollRef?: Ref<HTMLDivElement>;
};

/**
 * The standalone "Required actions" card — the bottom-LEFT cell of the 2×2 prediction grid
 * (under the Why breakdown, beside the Plan Summary). Since the redesign moved the
 * Now → Projected → Target verdict + cost into the hero, this component no longer renders the
 * plan-summary card — it's just the itemized list of concrete staffing moves (per item: the
 * "schedule N more hours" line, its marginal points from the cumulative pass, its projected
 * $ cost, and a remove control). Returns null for an empty plan, so its grid cell simply
 * doesn't render (no empty bottom-left cell).
 */
export const ActionItems: FC<Props> = ({
	selectedPlan,
	categories,
	onRemove,
	census,
	quarterDays,
	quarterWeekendDays,
	daysRemaining,
	periodMode,
	scrollRef,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const accent = theme.palette.primary.main;
	const reduceMotion = useReducedMotion();
	// Cumulative-scoring pass (FE analog of the BE greedy's `_to_steps`): credit each
	// plan item its marginal point gain over the items added BEFORE it, walking
	// `selectedPlan` in add-order. Single source of truth for every per-item points figure.
	// Hook runs before the empty-plan early return to keep hook order stable.
	const cumulative = useMemo(() => computeCumulativePass(selectedPlan, categories), [selectedPlan, categories]);
	if (selectedPlan.length === 0) return null;

	// Per-item projected dollar cost — `item.spend × census × applicableQuarterDays`.
	// Mode-INVARIANT: the same total HPPD lift requires the same total dollars whether
	// spread across the full quarter or compressed into the remaining catch-up window.
	const itemPrice = (item: PlanItem): number => {
		const days = WEEKEND_REC_IDS.has(item.id) ? quarterWeekendDays : quarterDays;
		return item.spend * census * days;
	};

	// Effective calendar-day window the displayed PPD averages over. Quarter mode uses all
	// quarter days; QTD mode uses only the remaining catch-up window, so the per-PPD rate
	// scales up. Guards `daysRemaining === 0` (completed quarter) by using the quarter count.
	const effDays = periodMode === "qtd" && daysRemaining > 0 ? daysRemaining : quarterDays;
	const itemDisplayPPD = (item: PlanItem): number => {
		if (census <= 0 || effDays <= 0) return 0;
		return itemPrice(item) / (census * effDays);
	};

	return (
		<Paper
			ref={scrollRef}
			elevation={0}
			sx={{ borderRadius: 3, overflow: "hidden", boxShadow: cardShadow(theme), scrollMarginTop: 16 }}
			data-testid="pbj-model-action-plan"
		>
			<Box sx={{ p: 1.5, px: 2, bgcolor: "background.default", borderBottom: "1px solid", borderColor: "divider" }}>
				<PanelHeader
					icon={<PlaylistAddCheckIcon />}
					title={t("pbj_model.action_plan.required_actions")}
					endingContent={
						<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
							{t("pbj_model.action_plan.item_count", { count: selectedPlan.length })}
						</Typography>
					}
				/>
			</Box>
			<Box>
				<AnimatePresence initial={false}>
					{selectedPlan.map((item, index) => {
						// Per-item marginal points from the cumulative pass — credited over the items
						// added before this one.
						const itemResult = cumulative.byItemId[item.id];
						const itemPoints = itemResult?.points ?? 0;
						// In "Quarter to date" mode the staffing change is delivered only over the
						// REMAINING applicable days, so the per-day HPPD bump during that window is
						// higher by `hoursCatchup / hours`. Derive the multiplier from existing fields.
						const hoursCatchup = item.hoursCatchup;
						const isCatchupMode = periodMode === "qtd" && hoursCatchup !== null;
						const hppdMultiplier = isCatchupMode && hoursCatchup !== null && item.hours > 0 ? hoursCatchup / item.hours : 1;
						// Per-week hours: weekend items add hours only on Sat+Sun (2 days/week);
						// every-day items add hours on all 7. `item.hours` is per-applicable-day.
						const daysPerWeek = WEEKEND_REC_IDS.has(item.id) ? DAYS_PER_WEEK_WEEKEND : DAYS_PER_WEEK_FULL;
						const hoursPerWeek = item.hours * daysPerWeek;
						const hoursCatchupPerWeek = item.hoursCatchup !== null ? item.hoursCatchup * daysPerWeek : null;
						const itemTotalPrice = itemPrice(item);
						const itemTotalPriceFormatted = itemTotalPrice.toLocaleString(undefined, { maximumFractionDigits: 0 });
						return (
							// Rows animate in (fade + small lift, staggered) and out (faster, with a height
							// collapse so the list closes up) via AnimatePresence + `layout`. GPU props only;
							// reduced-motion drops the movement (mount/unmount become instant). The divider
							// lives INSIDE each row's motion wrapper (top border, not first) so it collapses
							// with the row on exit instead of orphaning.
							<motion.div
								key={item.id}
								layout={!reduceMotion}
								initial={reduceMotion ? false : { opacity: 0, y: 6 }}
								animate={{ opacity: 1, y: 0 }}
								// Faster exit than enter (asymmetric timing) + a height collapse so the list closes up.
								exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0, transition: { duration: MOTION.DURATION.OVERLAY_FADE / 1000 } }}
								transition={
									reduceMotion
										? { duration: 0 }
										: {
												duration: MOTION.DURATION.ENTRANCE_MD / 1000,
												ease: [...MOTION.EASE_OUT_POINTS],
												delay: Math.min(index * MOTION.STAGGER.STEP, MOTION.STAGGER.MAX) / 1000,
											}
								}
								style={{ overflow: "hidden" }}
							>
								{index > 0 && <Divider />}
								<Stack
									direction="row"
									sx={{ p: 2, alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}
									data-testid={`pbj-model-action-item-${item.id}`}
								>
									<Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start", minWidth: 0 }}>
										<AccentIconTile size="xs" sx={{ mt: 0.25 }}>
											<CheckCircleIcon sx={{ fontSize: 18 }} />
										</AccentIconTile>
										<Box sx={{ minWidth: 0 }}>
											<Typography variant="body2" sx={{ fontWeight: "bold" }}>
												{periodMode === "qtd" && item.hoursCatchup === null
													? // No remaining days of the applicable type — catch-up infeasible.
														t("pbj_model.action_plan.catchup_infeasible")
													: t(periodMode === "qtd" ? item.actionCatchupKey : item.actionKey, {
															hours: periodMode === "qtd" && hoursCatchupPerWeek !== null ? hoursCatchupPerWeek : hoursPerWeek,
														})}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{t(item.titleKey)} • {t("pbj_model.recommendations.impact_hppd", { value: (item.impact * hppdMultiplier).toFixed(3) })}
											</Typography>
											{item.breakdown && item.breakdown.length > 1 && (
												<Stack
													spacing={0.25}
													sx={{ mt: 0.75, pl: 1, borderLeft: "2px solid", borderLeftColor: "divider" }}
													data-testid={`pbj-model-action-breakdown-${item.id}`}
												>
													{item.breakdown.map((b) => {
														// Per-category marginal points from the cumulative pass.
														const catPoints = itemResult?.pointsByCategory[b.categoryId] ?? 0;
														return (
															<Typography key={b.categoryId} variant="caption" color="text.secondary" sx={{ display: "block" }}>
																{b.categoryTitle}: {t("pbj_model.recommendations.impact_hppd", { value: (b.hppdGap * hppdMultiplier).toFixed(3) })}
																{" • "}
																<Box component="span" sx={{ fontWeight: 600, color: catPoints > 0 ? accent : "text.secondary" }}>
																	{t("pbj_model.recommendations.impact_points", { count: catPoints })}
																</Box>
															</Typography>
														);
													})}
												</Stack>
											)}
										</Box>
									</Stack>
									<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexShrink: 0 }}>
										<Box sx={{ textAlign: "right" }}>
											<Typography variant="body2" sx={{ fontWeight: "bold", color: accent }}>
												{t("pbj_model.recommendations.impact_points", { count: itemPoints })}
											</Typography>
											{/* Per-item Spend PPD, normalized to a per-CALENDAR-day rate over the window
									    the current period mode measures. Tooltip shows the raw $/applicable-PPD
									    the Recommendations slider controls. */}
											<Tooltip
												arrow
												placement="left"
												title={
													WEEKEND_REC_IDS.has(item.id)
														? t("pbj_model.action_plan.weekend_spend_tooltip", {
																value: item.spend.toFixed(2),
																weekendDays: quarterWeekendDays,
																quarterDays: periodMode === "qtd" && daysRemaining > 0 ? daysRemaining : quarterDays,
															})
														: ""
												}
											>
												<Typography variant="caption" color="text.secondary" sx={{ cursor: WEEKEND_REC_IDS.has(item.id) ? "help" : "default" }}>
													{t("pbj_model.recommendations.spend_format", { value: itemDisplayPPD(item).toFixed(2) })}
												</Typography>
											</Tooltip>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ display: "block", fontWeight: 600 }}
												data-testid={`pbj-model-action-item-cost-${item.id}`}
											>
												{t("pbj_model.recommendations.cost_total", { value: itemTotalPriceFormatted })}
											</Typography>
										</Box>
										<IconButton
											size="small"
											onClick={() => onRemove(item.id)}
											sx={{ color: "text.secondary", "&:hover": { color: "error.main", bgcolor: alpha(theme.palette.error.main, 0.08) } }}
											aria-label={t("pbj_model.action_plan.remove")}
											data-testid={`pbj-model-action-remove-${item.id}`}
										>
											<CloseIcon sx={{ fontSize: 18 }} />
										</IconButton>
									</Stack>
								</Stack>
							</motion.div>
						);
					})}
				</AnimatePresence>
			</Box>
		</Paper>
	);
};
